import { useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums.js';
import { createPersonBillingAccount, postEppRedeemTerminate } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { t, vatPercentageMsg } from '../../common/i18n/index.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { getPriceWithTax, getVatAsString } from '../../common/utils/taxUtils.js';
import { EppRedeemTerminateRequestType } from '../../generated/api/models.js';
import { closeDialog, openDialog, setDialogSubmitFailed, setDialogSubmitting } from '../../public/site/dialogStore';
import { addNotification } from '../../public/site/notificationStore.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { EppModificationPrices, PostEppRedeemTerminateRequest, Subscription } from '../../generated/api/models.js';
import type { PersonalBillingDetailsData } from '../PersonalBillingDetails/PersonalBillingDetails.js';

export const getRedeemPrice = (
  redeemType: EppRedeemTerminateRequestType,
  eppModificationPrices: EppModificationPrices
) => {
  return redeemType === EppRedeemTerminateRequestType.EMPLOYEE_REDEEM ||
    redeemType === EppRedeemTerminateRequestType.EMPLOYEE_REDEEM_BY_ADMIN
    ? `${formatSum(getPriceWithTax(eppModificationPrices.redeemOC!.totalEmployeePrice!))} (${t.U8AN('incl.')} ${t.A0OJ(
        vatPercentageMsg,
        getVatAsString()
      )})`
    : `${formatSum(eppModificationPrices.redeemOC!.totalCorporatePrice)} (${t.A0OJ(vatPercentageMsg, '0')})`;
};

export const useAdminEppRedeem = (
  redeemType: EppRedeemTerminateRequestType,
  sub: Subscription,
  baId: string
): (() => void) => {
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId: string }>();

  const payload: PostEppRedeemTerminateRequest = {
    requestType: redeemType,
    companyRedeem: redeemType === EppRedeemTerminateRequestType.COMPANY_REDEEM ? { billingAccountId: baId } : undefined,
  };

  return async () => {
    try {
      setDialogSubmitting(true);
      await postEppRedeemTerminate(sub.subscriptionId, payload, mdmId);
      addNotification(t.KNS9('Redemption request sent successfully'));
      closeDialog();
      closeDialog();
      navigate(paths.PS_DEVICES);
    } catch {
      setDialogSubmitFailed();
    }
  };
};

export const useAdminEppRedeemDialog = (
  redeemType: EppRedeemTerminateRequestType,
  sub: Subscription,
  baId: string,
  companyName: string
): (() => void) => {
  const postAdminEppRedeem = useAdminEppRedeem(redeemType, sub, baId);

  return () => {
    openDialog({
      onConfirm: postAdminEppRedeem,
      subscription: sub,
      payerName:
        redeemType === EppRedeemTerminateRequestType.COMPANY_REDEEM ? companyName : sub.subscriptionContactName!,
      redeemType: redeemType,
      type: DialogType.EPP_REDEEM,
    });
  };
};

export const useEmployeeEppRedeem = (
  sub: EmployeeSubscription,
  baId?: string,
  approverContact?: string,
  personalBillingDetails?: PersonalBillingDetailsData
): (() => void) => {
  const navigate = useNavigate();
  let personBaId = baId || 'NEW_PERSON_BA';
  const hasEmployeePrice = !!sub.details!.device!.eppModificationPrices!.redeemOC?.totalEmployeePrice;

  return async () => {
    try {
      setDialogSubmitting(true);
      if (personalBillingDetails?.personalBillingAddress && personalBillingDetails?.email) {
        const personBaResp = await createPersonBillingAccount({
          address: personalBillingDetails.personalBillingAddress,
          email: personalBillingDetails.email,
        });
        personBaId = personBaResp.billingAccountId!;
      }

      const payload: PostEppRedeemTerminateRequest = {
        employeeRedeem: {
          approverContact: approverContact || '',
          personBillingAccountId: hasEmployeePrice ? personBaId : undefined,
        },
        requestType: EppRedeemTerminateRequestType.EMPLOYEE_REDEEM,
      };
      await postEppRedeemTerminate(sub.subscriptionId, payload);
      addNotification(t.KNS9('Redemption request sent successfully'));
      closeDialog();
      navigate(paths.EMPLOYEE_DEVICES);
    } catch {
      setDialogSubmitFailed();
    }
  };
};

export const useEmployeeEppRedeemDialog = (
  sub: EmployeeSubscription,
  baId?: string,
  approverContact?: string,
  personalBillingDetails?: PersonalBillingDetailsData,
  firstName?: string,
  lastName?: string
): (() => void) => {
  const postEmployeeEppRedeem = useEmployeeEppRedeem(sub, baId, approverContact, personalBillingDetails);

  return () => {
    openDialog({
      onConfirm: postEmployeeEppRedeem,
      subscription: sub,
      payerName: `${firstName} ${lastName}`,
      redeemType: EppRedeemTerminateRequestType.EMPLOYEE_REDEEM,
      type: DialogType.EPP_REDEEM,
    });
  };
};
